/**
 * Einmal-Migration der Listen aus der Punkte-DB (`bpweb-punkte`) in die neue
 * zentrale Listen-DB (`bpweb-listen`).
 *
 * Hintergrund: Historisch wurden editierte Listen als `list_<name>`-Dokumente in
 * der Punkte-DB abgelegt. Mit der Listen-DB-Migration werden sie in die eigene
 * `bpweb-listen` überführt. Diese Funktion ist:
 * - **idempotent** (Marker-Dokument verhindert Doppel-Migration),
 * - **quell-schonend** (die Punkte-DB wird NUR gelesen, nie verändert),
 * - **IP-schonend** (ein `allDocs`-Bulk + ein `bulkDocs`, keine Schleifen-Saves).
 *
 * Per-Browser-`userList_*`-Edits aus dem LocalStorage werden bewusst NICHT
 * migriert: autoritativ sind allein die geteilten `list_*`-Dokumente aus der DB.
 */

const LIST_DOC_PREFIX = 'list_';
const RANGE_END = '￿';
export const LIST_MIGRATION_MARKER = '_local/list_migration_done';

export interface ListMigrationResult {
  /** IDs der neu übertragenen Listen-Dokumente. */
  migrated: string[];
  /** IDs, die im Ziel bereits existierten und übersprungen wurden. */
  skipped: string[];
  /** true, wenn der Marker bereits gesetzt war und nichts getan wurde. */
  alreadyDone: boolean;
}

/**
 * Führt die Migration aus.
 * @param source Quelle (Punkte-DB) — wird nur gelesen.
 * @param target Ziel (Listen-DB) — erhält die Listen + Marker.
 */
export async function migrateListsToListenDb(source: any, target: any): Promise<ListMigrationResult> {
  // Marker prüfen → idempotent
  const marker = await target.get(LIST_MIGRATION_MARKER).catch(() => null);
  if (marker) {
    return { migrated: [], skipped: [], alreadyDone: true };
  }

  // Alle list_*-Dokumente aus der Quelle (ein Bulk-Request)
  const res = await source.allDocs({
    include_docs: true,
    startkey: LIST_DOC_PREFIX,
    endkey: LIST_DOC_PREFIX + RANGE_END,
  });

  const sourceDocs = ((res?.rows || []) as any[])
    .map(r => r?.doc)
    .filter(d => d && d.type === 'list' && Array.isArray(d.data));

  const migrated: string[] = [];
  const skipped: string[] = [];
  const toWrite: any[] = [];

  for (const d of sourceDocs) {
    const exists = await target.get(d._id).catch(() => null);
    if (exists) {
      skipped.push(d._id);
      continue;
    }
    toWrite.push({
      _id: d._id,
      type: 'list',
      listName: d.listName,
      data: d.data,
      updatedAt: d.updatedAt ?? null,
      updatedBy: d.updatedBy ?? null,
      migratedFrom: 'bpweb-punkte',
    });
    migrated.push(d._id);
  }

  if (toWrite.length > 0) {
    await target.bulkDocs(toWrite);
  }

  // Marker setzen (auch wenn nichts zu übertragen war) → läuft genau einmal.
  await target.put({
    _id: LIST_MIGRATION_MARKER,
    type: 'migration',
    migratedAt: new Date().toISOString(),
    migratedCount: migrated.length,
    skippedCount: skipped.length,
  });

  return { migrated, skipped, alreadyDone: false };
}
